import { extractAppConfig } from '@/utils/app-config';
import { logger } from '@rhg/libs/logger';
import nextjsDefaultLoader from 'next/dist/shared/lib/image-loader';
// eslint-disable-next-line no-restricted-imports
import type { ImageLoader, ImageLoaderProps } from 'next/image';

// Hostnames of the Calm CDN that support the `/{width}/` path-prefix resize
// transform. Sourced from app config (NEXT_PUBLIC_OPTIMIZABLE_IMAGE_CDN_HOSTS) so the
// host list lives in one place rather than being hardcoded across files.
// Read once at module scope: the loader runs per-image and extractAppConfig()
// rebuilds the whole config object on each call.
const { NEXT_PUBLIC_OPTIMIZABLE_IMAGE_CDN_HOSTS: IMAGE_CDN_HOSTS } =
    extractAppConfig();

const isResizableCalmAssetUrl = (src: string): boolean => {
    // Match on the parsed hostname (exact), not a substring of the whole URL,
    // so a host appearing in a path/query (e.g. a proxy `?src=...assets.calm.com`)
    // doesn't wrongly qualify. Mirrors the hostname check inside `calmLoader`.
    try {
        return IMAGE_CDN_HOSTS.includes(new URL(src).hostname);
    } catch {
        return false;
    }
};

const calmLoader = ({ src, width }: ImageLoaderProps): string | undefined => {
    let parsedSrc;
    try {
        parsedSrc = new URL(src);
    } catch (err) {
        logger.error(
            'Failed to parse src on `next/image`, if using relative image it must start with a leading slash "/" or be an absolute URL (http:// or https://)',
            { src },
            err as Error
        );
        return undefined;
    }
    if (!IMAGE_CDN_HOSTS.includes(parsedSrc.hostname)) {
        return undefined;
    }
    const pathnamePieces = parsedSrc.pathname.split('/');
    // The first path segment is an existing resize transform only when it's a
    // bare width ("2000") or width-x-height ("2000x2000"). Otherwise the whole
    // path is the asset key — including folder-style keys like
    // "thumbnails/hash.jpg" — so inject the requested width as a new leading
    // segment. The numeric test guards against a non-numeric first segment
    // becoming `Math.min(NaN, width)` → "/NaN/" → a 404.
    const firstSegment = pathnamePieces[1] ?? '';
    if (/^\d+(x\d+)?$/.test(firstSegment)) {
        // the calm assets service will upsize images,
        // so we have to take the Math.min ourselves client-side
        const srcWidth = parseInt(firstSegment, 10);
        pathnamePieces[1] = `${Math.min(srcWidth, width)}`;
    } else {
        pathnamePieces.splice(1, 0, `${width}`);
    }
    parsedSrc.pathname = pathnamePieces.join('/');
    return parsedSrc.toString();
};

const loader: ImageLoader = ({ src, width: _width, quality }) => {
    const width = Math.floor(_width);

    if (isResizableCalmAssetUrl(src)) {
        const calmUrl = calmLoader({ src, width, quality });
        if (calmUrl) {
            return calmUrl;
        }
    }

    // Fall back to the default next loader
    // Note: we have to hard-code the config here (from next.config.js)
    // as it's not available at runtime
    const config = {
        // The reward svg images access a separate bucket when in non-production environments that require us to use these settings
        ...(process.env.NEXT_PUBLIC_ENV !== 'production'
            ? {
                  dangerouslyAllowSVG: true,
                  contentDispositionType: 'attachment' as const,
                  contentSecurityPolicy:
                      "default-src 'self'; script-src 'none'; sandbox;",
              }
            : {}),
        remotePatterns: [
            {
                protocol: 'https' as const,
                hostname: '**.calm.com',
            },
            {
                protocol: 'https' as const,
                hostname: '**.calmhealth.com',
            },
            {
                protocol: 'https' as const,
                hostname: 'res.cloudinary.com',
                pathname: '/calm-com/**',
            },
            // The reward svg images access a separate bucket when in non-production environments that require us to use these settings
            ...(process.env.NEXT_PUBLIC_ENV !== 'production'
                ? [
                      {
                          protocol: 'https' as const,
                          hostname: 'storage.googleapis.com',
                          pathname: '/evexia-staging-static-assets/**',
                      },
                  ]
                : []),
        ],
        domains: [],
        loader: 'custom' as const,
        loaderFile: './src/utils/images/imageLoader.ts',
        path: '/_next/image',
    };

    const safeSrc = /^\//.test(src) || /^http/.test(src) ? src : `/${src}`;
    return nextjsDefaultLoader({
        config,
        src: safeSrc,
        width: _width,
        quality,
    });
};

export default loader;

/**
 * Returns a Calm CDN URL (any host in `NEXT_PUBLIC_OPTIMIZABLE_IMAGE_CDN_HOSTS`,
 * e.g. `assets.calm.com` / `assets-prod-usw.calm.com`) rewritten to request
 * `width` pixels, for use in contexts that can't go through `next/image` — e.g.
 * CSS `background-image` URLs on blurred banner backgrounds, where the image
 * must stay in CSS so the blur filter applies. URLs on other hosts (and
 * unparseable ones) are returned unchanged.
 */
export const getSizedCalmAssetUrl = (src: string, width: number): string => {
    if (isResizableCalmAssetUrl(src)) {
        const calmUrl = calmLoader({ src, width: Math.floor(width) });
        if (calmUrl) {
            return calmUrl;
        }
    }
    return src;
};

export const imageMatches = (input: string, expected: string): boolean => {
    const transformed = loader({ src: input, width: 3840 });
    return transformed === expected;
};
